import React from 'react';
import {ListItemKeySelector} from 'react-window';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {
    frameToSMPTE,
    FrameRate,
} from '@accurate-player/accurate-player-core';
import ListTable, {
    ListTableColumn,
    OnRowClickCallback,
} from '@pluto/player-list-table';
import {
    OnClickEvent,
    OnMouseButtonEvent,
    OnSelectChangeEvent,
} from '@pluto/@types';
import {TimespanCategory} from '@pluto/rest';
import QCTabsSelect from 'VideoAudioQC/QCTabs/QCTabsSelect/QCTabsSelect';
import TimecodeInput from 'VideoAudioQC/QCTabs/TimecodeInput/TimecodeInputContainer';
import {
    getCategoryLabel,
    getMarkupMenuItems,
    MenuItems,
} from '../../IssueSelectMenuItems/IssueSelectMenuItems';
import {MarkupMarker} from '../../../Timeline/TimelineTypes';

import {
    OnButtonClick,
    OnTypeChange,
    OnFrameChange,
} from './TableContainer';

import './Table.scss';

export type MarkupData = {
    identifier: string;
    issue: number;
    start: number;
    end: number;
    startSMPTE: string;
    endSMPTE: string;
}

export interface TableProps {
    markers: MarkupMarker[];
    noteTypes: TimespanCategory[];
    selectedMarkerId: string;
    selectedColumn: string;
    frameRate: FrameRate;
    dropFrame: boolean;
    duration: number;
    onRowClick: OnRowClickCallback;
    onDeleteClick: OnButtonClick;
    onTypeChange: OnTypeChange;
    onStartFrameChange: OnFrameChange;
    onEndFrameChange: OnFrameChange;
    jobDeliveryDate: string;
}

const mapMarkupData = curry((props: TableProps, marker: MarkupMarker): MarkupData => {
    const {
        frameRate,
        dropFrame,
    } = props;
    const {
        numerator,
        denominator,
    } = frameRate;
    const {
        identifier,
        start,
        end,
        issue,
    } = marker;

    const frameRateValue = numerator / denominator;
    const startSMPTE = frameToSMPTE(start, frameRateValue, dropFrame);
    const endSMPTE = frameToSMPTE(end, frameRateValue, dropFrame);

    return {
        identifier,
        issue,
        startSMPTE,
        start,
        end,
        endSMPTE,
    };
});

export const onMarkerTypeChange = wrap((markupData: MarkupData, onTypeChange: OnTypeChange, event: OnSelectChangeEvent) => {
    const type = event.target.value as number;

    onTypeChange(markupData, type);
});

export interface TypeSelectProps {
    selectedMarkerId: string,
    selectedColumn: string,
    columnKey: string,
    noteTypes: TimespanCategory[],
    onTypeChange: OnTypeChange,
}

const emptyLabel = 'No reason selected';

export const getTypeField = curry((props: TypeSelectProps, markupData: MarkupData) => {
    const {
        selectedMarkerId,
        selectedColumn,
        columnKey,
        noteTypes,
        onTypeChange,
    } = props;
    const {
        identifier,
        issue,
    } = markupData;

    if (selectedMarkerId === identifier) {
        const isClicked = selectedColumn === columnKey;
        const issues: MenuItems[] = getMarkupMenuItems(noteTypes);
        const handleOnChange = onMarkerTypeChange(markupData, onTypeChange);

        return (
            <QCTabsSelect
                onChange={handleOnChange}
                menuItems={issues}
                currentValue={issue}
                autoFocus={isClicked}
                emptyLabel={emptyLabel}
                id="type-select"
            />
        );
    }

    return getCategoryLabel(issue, noteTypes, emptyLabel);
});

export interface TimecodeFieldProps {
    dropFrame: boolean,
    duration: number,
    frameRateValue: number,
    selectedMarkerId: string,
    selectedColumn: string,
    columnKey: string,
    onInputClick: OnClickEvent,
    onFrameChange: OnFrameChange,
}

export const onStartTimecodeChange = wrap((onStartFrameChange: OnFrameChange, data: MarkupData, start: number) => {
    onStartFrameChange(data, start);
});

export const getStartTimecodeField = curry((props: TimecodeFieldProps, data: MarkupData) => {
    const {
        dropFrame,
        duration,
        frameRateValue,
        selectedMarkerId,
        selectedColumn,
        columnKey,
        onInputClick,
        onFrameChange,
    } = props;
    const {
        identifier,
        start,
        startSMPTE,
    } = data;

    if (selectedMarkerId === identifier) {
        const isClicked = selectedColumn === columnKey;
        const handleStartFrameChange = onStartTimecodeChange(onFrameChange, data);

        return (
            <TimecodeInput
                frame={start}
                dropFrame={dropFrame}
                duration={duration}
                frameRateValue={frameRateValue}
                autoFocus={isClicked}
                onFrameChange={handleStartFrameChange}
                onClick={onInputClick}
            />
        );
    }

    return startSMPTE;
});

export const onEndTimecodeChange = wrap((onEndFrameChange: OnFrameChange, data: MarkupData, end: number) => {
    onEndFrameChange(data, end);
});

export const getEndTimecodeField = curry((props: TimecodeFieldProps, data: MarkupData) => {
    const {
        dropFrame,
        duration,
        frameRateValue,
        selectedMarkerId,
        selectedColumn,
        columnKey,
        onInputClick,
        onFrameChange,
    } = props;
    const {
        identifier,
        end,
        endSMPTE,
    } = data;

    if (selectedMarkerId === identifier) {
        const isClicked = selectedColumn === columnKey;
        const handleEndFrameChange = onEndTimecodeChange(onFrameChange, data);

        return (
            <TimecodeInput
                frame={end}
                dropFrame={dropFrame}
                duration={duration}
                frameRateValue={frameRateValue}
                autoFocus={isClicked}
                onFrameChange={handleEndFrameChange}
                onClick={onInputClick}
            />
        );
    }

    return endSMPTE;
});

export const onButtonClick = wrap((markerData: MarkupData, onClick: OnButtonClick, event: OnMouseButtonEvent) => {
    event.stopPropagation();
    event.preventDefault();

    onClick(markerData);
});

export const onInputClick = (event: React.MouseEvent) => {
    event.stopPropagation();
};

export const getTable = (props: TableProps) => {
    const {
        markers,
        noteTypes,
        selectedMarkerId,
        selectedColumn,
        frameRate,
        dropFrame,
        duration,
        onTypeChange,
        onStartFrameChange,
        onEndFrameChange
    } = props;

    const handleDataMap = mapMarkupData(props);
    const data = markers.map(handleDataMap);

    const typeProps: TypeSelectProps = {
        columnKey: 'type',
        selectedColumn,
        selectedMarkerId,
        noteTypes,
        onTypeChange,
    };
    const render = getTypeField(typeProps);

    const {
        numerator,
        denominator,
    } = frameRate;
    const frameRateValue = numerator / denominator;

    const renderStart = getStartTimecodeField({
        dropFrame,
        duration,
        frameRateValue,
        selectedMarkerId,
        selectedColumn,
        columnKey: 'startSMPTE',
        onInputClick,
        onFrameChange: onStartFrameChange,
    });

    const renderEnd = getEndTimecodeField({
        dropFrame,
        duration,
        frameRateValue,
        selectedMarkerId,
        selectedColumn,
        columnKey: 'endSMPTE',
        onInputClick,
        onFrameChange: onEndFrameChange,
    });

    const columns: ListTableColumn[] = [
        {
            dataKey: 'type',
            label: 'Type',
            render,
        },
        {
            dataKey: 'startSMPTE',
            label: 'In',
            render: renderStart,
        },
        {
            dataKey: 'endSMPTE',
            label: 'Out',
            render: renderEnd,
        },
    ];
    const emptyText = 'No markers added yet';

    return (
        <ListTable
            data={data}
            columns={columns}
            headerHeight={20}
            rowHeight={40}
            emptyText={emptyText}
            itemKey={getItemKey}
            selectedRowKey={selectedMarkerId}
            onRowClick={() => {}}
        />
    );
};

export const getItemKey: ListItemKeySelector = (index: number, data: {items: MarkupData[]}) => {
    return data.items[index].identifier;
};

export default function Table(props: TableProps) {
    const table = getTable(props);

    return (
        <div className="markup-table-container">
            {table}
        </div>
    );
}
